// BasicInfoStep.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { 
  TextField, 
  Grid, 
  MenuItem, 
  Typography, 
  FormHelperText
} from '@mui/material';
import { SignupData } from 'form-application-shared';

const productOptions = [
  "Collateral Protection Insurance (CPI)",
  "Damage Waiver (CDW)",
  "Debt Cancellation (DCC)",
  "GAP",
  "Post Sale Inspection (PSI)",
  "Prepaid Maintenance (PPM)",
  "Service Contract (VSC)",
  "Vendor Single Interest (VSI)",
  "Warranty"
];

interface BasicInfoStepProps {
  formData: SignupData;
  setFormData: React.Dispatch<React.SetStateAction<SignupData>>;
  onValidationChange: (validationFn: () => boolean) => void;
}

interface BasicInfoErrors {
  reinsuranceName1?: string;
  reinsuranceName2?: string;
  reinsuranceName3?: string;
  products?: string;
}

const BasicInfoStep: React.FC<BasicInfoStepProps> = ({ formData, setFormData, onValidationChange }) => {
  const [errors, setErrors] = useState<BasicInfoErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Validation function
  const validateStep = useCallback(() => {
    let isValid = true;
    const newErrors: BasicInfoErrors = {};

    // Always validate product selection
    if (!formData.products || formData.products.length === 0) {
      newErrors.products = 'At least one product must be selected';
      isValid = false;
    }

    // Always validate reinsurance name 1
    if (!formData.reinsuranceName1) {
      newErrors.reinsuranceName1 = 'Reinsurance Name 1 is required';
      isValid = false;
    }

    // Always validate reinsurance name 2
    if (!formData.reinsuranceName2) {
      newErrors.reinsuranceName2 = 'Reinsurance Name 2 is required';
      isValid = false;
    }

    // Always validate reinsurance name 3
    if (!formData.reinsuranceName3) {
      newErrors.reinsuranceName3 = 'Reinsurance Name 3 is required';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  }, [formData.products, formData.reinsuranceName1, formData.reinsuranceName2, formData.reinsuranceName3]);

  // Add a function to mark all fields as touched
  const markAllFieldsAsTouched = () => {
    const allFields: Record<string, boolean> = {
      products: true,
      reinsuranceName1: true,
      reinsuranceName2: true,
      reinsuranceName3: true
    };
    
    setTouched(allFields);
  };

  // Create a validation wrapper that marks all fields as touched when validating
  const validateStepAndMarkTouched = useCallback(() => {
    markAllFieldsAsTouched();
    return validateStep();
  }, [validateStep]);

  // Register validation function with parent component
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(validateStepAndMarkTouched);
    }
  }, [onValidationChange, validateStepAndMarkTouched]);

  // Validate on each change to provide real-time feedback,
  // but only after fields have been touched
  useEffect(() => {
    validateStep();
  }, [formData.products, formData.reinsuranceName1, formData.reinsuranceName2, formData.reinsuranceName3, validateStep]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Mark field as touched when changed
    setTouched((prev: Record<string, boolean>) => ({
      ...prev,
      [name]: true
    }));
    
    // Type-safe update for form data
    setFormData((prev: SignupData) => {
      // Create a copy of the form data
      const newFormData = { ...prev };
      // Use type assertion for the dynamic property assignment
      (newFormData as any)[name] = value;
      return newFormData;
    });
  };

  const handleProductsChange = (e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    const { value } = e.target;
    
    // Mark products field as touched
    setTouched((prev: Record<string, boolean>) => ({
      ...prev,
      products: true
    }));
    
    setFormData((prev: SignupData) => ({
      ...prev,
      products: Array.isArray(value) ? value : typeof value === "string" ? value.split(",") : []
    }));
  };

  // Function to get full reinsurance name with suffix
  const getFullReinsuranceName = (baseName: string): string => {
    return baseName ? `${baseName} Reinsurance, Inc.` : '';
  };

  return (
    <>
      <Typography variant="h5" gutterBottom>
        Basic Reinsurance Info
      </Typography>
      <Grid container spacing={2}>
        {/* Always show products dropdown */}
        <Grid item xs={12}>
          <TextField
            select
            label="Products"
            name="products"
            SelectProps={{ multiple: true }}
            value={formData.products || []}
            onChange={handleProductsChange}
            fullWidth
            required
            error={touched.products && !!errors.products}
            helperText={touched.products && errors.products}
          >
            {productOptions.map((product) => (
              <MenuItem key={product} value={product}>
                {product}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        
        {/* Always show reinsurance names */}
        <Grid item xs={12}>
          <TextField
            label="Reinsurance Name 1"
            name="reinsuranceName1"
            value={formData.reinsuranceName1 || ""}
            onChange={handleChange}
            fullWidth
            required
            error={touched.reinsuranceName1 && !!errors.reinsuranceName1}
            helperText={touched.reinsuranceName1 && errors.reinsuranceName1}
          />
          {formData.reinsuranceName1 && (
            <FormHelperText>
              Will be displayed as: <strong>{getFullReinsuranceName(formData.reinsuranceName1)}</strong>
            </FormHelperText>
          )}
          {!formData.reinsuranceName1 && (
            <FormHelperText>
              Your entry will be appended with " Reinsurance, Inc."
            </FormHelperText>
          )}
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Reinsurance Name 2"
            name="reinsuranceName2"
            value={formData.reinsuranceName2 || ""}
            onChange={handleChange}
            fullWidth
            required
            error={touched.reinsuranceName2 && !!errors.reinsuranceName2}
            helperText={touched.reinsuranceName2 && errors.reinsuranceName2}
          />
          {formData.reinsuranceName2 && (
            <FormHelperText>
              Will be displayed as: <strong>{getFullReinsuranceName(formData.reinsuranceName2)}</strong>
            </FormHelperText>
          )}
          {!formData.reinsuranceName2 && (
            <FormHelperText>
              Your entry will be appended with " Reinsurance, Inc."
            </FormHelperText>
          )}
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Reinsurance Name 3"
            name="reinsuranceName3"
            value={formData.reinsuranceName3 || ""}
            onChange={handleChange}
            fullWidth
            required
            error={touched.reinsuranceName3 && !!errors.reinsuranceName3}
            helperText={touched.reinsuranceName3 && errors.reinsuranceName3}
          />
          {formData.reinsuranceName3 && (
            <FormHelperText>
              Will be displayed as: <strong>{getFullReinsuranceName(formData.reinsuranceName3)}</strong>
            </FormHelperText>
          )}
          {!formData.reinsuranceName3 && (
            <FormHelperText>
              Your entry will be appended with " Reinsurance, Inc."
            </FormHelperText>
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default BasicInfoStep;